/**
 * The free-shipping progress bar in the cart drawer and on the cart page.
 *
 * It states a threshold, so it is a per-visitor shipping claim and obeys the same invariant as
 * the announcement bar (docs/dynamic-shipping-claims.md): no country is ever assumed here. The
 * country, its threshold and the finished sentences all come from `assets/shipping-claim.js`,
 * which dispatches `shipping-claim:confirmed` only after positive confirmation (an explicit
 * country selection, or a settled detection) and only for a country with a fresh, complete,
 * EUR `shipping_info` entry carrying a threshold. Without that event this script renders
 * nothing - no default country, no fallback threshold.
 *
 * The bar goes into every `[data-shipping-progress-slot]`: empty placeholders the cart markup
 * renders directly under the multi-buy offer. Dawn re-renders that markup on every cart change,
 * which empties the slots again, so they are refilled from the last known cart. Text is written
 * with `textContent` only; the remaining amount gets its own element so it can be emphasised.
 */
(() => {
  const template = document.getElementById('ShippingProgressBar');
  if (!template || typeof window.fetch !== 'function') return;

  const SLOT_SELECTOR = '[data-shipping-progress-slot]';
  const MINIMUM_VISIBLE_PERCENT = 6;

  let claim = null;
  let cart = null;
  let fetching = false;
  let lastPercent = 0;

  /** 1250 -> "12,50", 1200 -> "12": the store's amount style (see snippets/shipping-amount.liquid). */
  const formatCents = (cents) => {
    const whole = Math.floor(cents / 100);
    const rest = cents % 100;
    return rest === 0 ? String(whole) : `${whole},${String(rest).padStart(2, '0')}`;
  };

  /**
   * Writes the sentence into `element`, wrapping the first occurrence of the amount (with its
   * euro sign when the copy puts one in front) in an emphasised span. Plain text nodes only.
   */
  const writeSentence = (element, sentence, amount) => {
    element.textContent = '';
    const withSign = `€ ${amount}`;
    const highlight = amount && sentence.indexOf(withSign) !== -1 ? withSign : amount;
    const at = highlight ? sentence.indexOf(highlight) : -1;
    if (at === -1) {
      element.textContent = sentence;
      return;
    }

    const emphasis = document.createElement('span');
    emphasis.className = 'rw-shipbar__amount';
    emphasis.textContent = highlight;
    element.append(sentence.slice(0, at), emphasis, sentence.slice(at + highlight.length));
  };

  const build = (total) => {
    const reached = total >= claim.threshold;
    const amount = reached ? '' : formatCents(claim.threshold - total);
    const sentence = claim.sentence(amount);
    if (sentence === '') return null;

    const percent = reached ? 100 : Math.max(MINIMUM_VISIBLE_PERCENT, Math.floor((total / claim.threshold) * 100));
    const wrapper = template.content.firstElementChild.cloneNode(true);
    writeSentence(wrapper.querySelector('[data-shipping-progress-text]'), sentence, amount);
    if (reached) wrapper.classList.add('rw-shipbar-wrap--reached');
    return { wrapper, percent };
  };

  /**
   * Starts the fill at the previous level and moves it to the new one on the next frame, so the
   * bar visibly grows (or shrinks) with each cart change instead of jumping.
   */
  const animate = (wrapper, percent) => {
    const fill = wrapper.querySelector('[data-shipping-progress-fill]');
    fill.style.setProperty('--rw-progress', `${lastPercent}%`);
    const next = () => fill.style.setProperty('--rw-progress', `${percent}%`);
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => window.requestAnimationFrame(next));
    } else {
      next();
    }
  };

  /** The bar the current cart warrants, or null. */
  const current = () => {
    if (!claim || !cart || !(cart.item_count > 0) || cart.currency !== claim.currency) return null;
    return build(cart.total_price);
  };

  const render = () => {
    const slots = document.querySelectorAll(SLOT_SELECTOR);
    const built = current();

    slots.forEach((slot) => {
      if (slot.firstChild) slot.textContent = '';
      if (!built) return;
      const wrapper = built.wrapper.cloneNode(true);
      slot.appendChild(wrapper);
      animate(wrapper, built.percent);
    });

    if (built && slots.length > 0) lastPercent = built.percent;
  };

  const refresh = () => {
    if (fetching) return;
    fetching = true;
    fetch(`${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}cart.js`, {
      headers: { Accept: 'application/json' },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        fetching = false;
        if (data && typeof data.total_price === 'number') {
          cart = data;
          render();
        }
      })
      .catch(() => {
        fetching = false;
      });
  };

  /** A slot Dawn has just re-rendered is empty while the cart still warrants a bar. */
  const hasEmptySlot = () => {
    if (!current()) return false;
    const slots = document.querySelectorAll(SLOT_SELECTOR);
    for (let index = 0; index < slots.length; index++) {
      if (!slots[index].firstElementChild) return true;
    }
    return false;
  };

  const observe = (element) => {
    if (!element || !window.MutationObserver) return;
    new MutationObserver(() => {
      if (hasEmptySlot()) render();
    }).observe(element, { childList: true, subtree: true });
  };

  const start = (detail) => {
    if (claim || !detail || typeof detail.sentence !== 'function') return;
    if (!Number.isInteger(detail.threshold) || detail.threshold <= 0) return;
    claim = detail;

    refresh();

    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          // A cart/change.js response is the whole cart; an add-to-cart response is only the
          // added item, so that one is re-read.
          if (event && event.cartData && typeof event.cartData.total_price === 'number' && 'item_count' in event.cartData) {
            cart = event.cartData;
            render();
          } else {
            refresh();
          }
        });
      }
    } catch (error) {
      // No pub/sub: the observers below still refill the slots, with the last known cart.
    }

    observe(document.querySelector('cart-drawer'));
    observe(document.getElementById('main-cart-footer'));
  };

  document.addEventListener('shipping-claim:confirmed', (event) => start(event.detail));
  if (window.ShippingClaimConfirmed) start(window.ShippingClaimConfirmed);
})();
